import * as Yup from "yup";
import i18next from "../../i18n";

/**
 * Values `TagForm` runs through React Hook Form + Yup — the first RHF+Yup form on this screen
 * (Task 4's brief). `color` is included here (unlike `UserGroupForm`'s membership, which stays a
 * plain `useState` because there's no rule to validate) because `ColorField` commits a plain hex
 * string to this same RHF field via `Controller`, and that string IS worth a real format rule —
 * see the schema below.
 */
export type TagFormValues = {
  name: string;
  description: string;
  color: string;
};

const HEX_COLOR_PATTERN = /^#[0-9A-Fa-f]{6}$/;

/**
 * Validation for the create/edit tag form (`POST` / `PUT /tags`), extracted out of
 * `TagForm.tsx` so each rule is testable without rendering the form — same reasoning
 * `userGroupValidationSchema.ts` documents.
 *
 * Three fields, two required (`name`, `color`) and one optional (`description`) — the majority-
 * required rule `TextareaField`'s `optional` marker exists for (see that component's docstring)
 * puts `description` in the marked minority.
 *
 * `.trim()` on `name` is load-bearing, not decorative, exactly as in `userGroupValidationSchema`:
 * it runs before `.required()`, which is what makes a whitespace-only name fail too.
 *
 * `color` needs BOTH `.required()` and `.matches()`, not just one — mutation-tested to confirm
 * neither is redundant with the other. `ColorField`'s custom `<input type="color">` always
 * submits a well-formed 6-digit hex string (the browser itself won't accept anything else — see
 * that component's own note on the HTML spec's lowercase-only sanitization), and every preset
 * swatch is a fixed, already-valid hex literal, so in practice this rule never fires against a
 * real user interaction. It's still real, testable defense: `.matches()` alone does not catch a
 * genuinely MISSING color (Yup skips a `.matches()`-only rule against `undefined` unless
 * `.required()` also runs), and `.required()` alone does not catch a malformed non-hex string —
 * each half only fails the case the other one lets through, confirmed by removing each in turn
 * (see this schema's own test file).
 */
export const tagFormValidationSchema = Yup.object().shape({
  name: Yup.string().trim().required(i18next.t("tags.nameRequired")),
  description: Yup.string(),
  color: Yup.string()
    .required(i18next.t("tags.colorRequired"))
    .matches(HEX_COLOR_PATTERN, i18next.t("tags.colorInvalid")),
});
